import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  Row,
  List,
  Divider,
  Input,
  message,
  Avatar,
  Button,
  InputNumber,
} from "antd";
import { useSelector } from "react-redux";
import { debounce } from "lodash";

import { Locale, ApiManager } from "../../utils";
import { getUserInfo } from "../../utils/redux/reducers/User";
import Base from "../Base";

const i18n = Locale.getInstance();
const api = ApiManager.getInstance();
const base = Base.getInstance();

function omit(obj, arr) {
  return Object.keys(obj)
    .filter((k) => !arr.includes(k))
    .reduce((acc, key) => ((acc[key] = obj[key]), acc), {}); //eslint-disable-line
}

function SendTicketForm({ selectedCoupon, onGetDataList, onCancel }) {
  const [keyword, onChangeKeyword] = useState("");
  const [loading, setLoading] = useState(false);
  const [usersList, setUsersList] = useState({ data: [] });
  const [selectedUserList, setSelectedUserList] = useState({});
  const user = useSelector((state) => getUserInfo(state));

  const onSearchUsers = useRef(() => {});

  onSearchUsers.current = debounce(async (page = 1) => {
    const { token } = user;
    if (token) {
      try {
        setLoading(true);
        const apiUrl = api.coupons.users;
        const apiConfig = {
          token,
          query: {
            "filter[name]": keyword,
            count: 8,
            page,
          },
        };
        const res = await apiUrl.run(apiConfig);
        if (res.result) {
          setUsersList(res.data);
        }
      } catch (error) {
        message.error(error.message);
      } finally {
        setLoading(false);
      }
    }
  }, 500);

  function onDeleteUser(userId) {
    const others = omit(selectedUserList, [`${userId}`]);
    setSelectedUserList(others);
  }

  function onSelectUser(item) {
    setSelectedUserList({
      ...selectedUserList,
      [item.id]: { ...item, count: 1 },
    });
  }

  function checkIfItemSelected(itemId) {
    const currentKeys = Object.keys(selectedUserList);
    return currentKeys.findIndex((key) => key === `${itemId}`) !== -1;
  }

  async function onSendTicket2Users() {
    const apiUrl = api.lottery.send;
    if (user && user.token) {
      try {
        const data = {};
        Object.keys(selectedUserList).forEach((key) => {
          data[key] = selectedUserList[key].count;
        });
        const apiConfig = {
          token: user.token,
          path: { id: selectedCoupon.id },
          data: {
            users: Object.keys(data).map((id) => ({
              user_id: id,
              tickets: data[id],
            })),
          },
        };
        setLoading(true);
        const res = await apiUrl.run(apiConfig);
        if (res.result) {
          base.showMessage(res.message, Base.Message.success);
          onCancel();
          onGetDataList();
        } else {
          base.showMessage(res.message);
        }
      } catch (error) {
        base.handleApiError(apiUrl, error);
      } finally {
        setLoading(false);
      }
    }
  }

  useEffect(() => {
    onSearchUsers.current();
  }, [onSearchUsers]);

  return (
    <Modal
      open={selectedCoupon !== null}
      onCancel={() => {
        setSelectedUserList({});
        onCancel();
      }}
      title={i18n.t("sales.coupons.send")}
      width={1020}
      okText={i18n.t("base.modal.okText")}
      cancelText={i18n.t("base.modal.cancelText")}
      onOk={() => {
        onSendTicket2Users();
      }}
      okButtonProps={{ loading }}
    >
      <Row align="top">
        <List
          loading={loading}
          style={{ width: "49%", height: "100%" }}
          header={
            <Input.Search
              enterButton
              value={keyword}
              onChange={(e) => onChangeKeyword(e.target.value)}
              onSearch={() => onSearchUsers.current()}
            />
          }
          pagination={{
            current: usersList.current_page,
            onChange: (page) => onSearchUsers.current(page),
            total: usersList.total,
            pageSize: 8,
            showSizeChanger: false,
            showQuickJumper: true,
          }}
          dataSource={usersList.data}
          renderItem={(item) => (
            <List.Item
              actions={[
                <Button
                  disabled={checkIfItemSelected(item.id)}
                  onClick={() => onSelectUser(item)}
                >
                  {checkIfItemSelected(item.id)
                    ? i18n.t("sales.coupons.selected")
                    : i18n.t("sales.coupons.select")}
                </Button>,
              ]}
            >
              <List.Item.Meta
                avatar={
                  <Avatar shape="circle" size="large" src={item.avatar} />
                }
                title={item.nickname}
                description={item.mobile}
              />
            </List.Item>
          )}
        />
        <Divider type="vertical" style={{ height: 500 }} />
        <List
          style={{ width: "49%" }}
          dataSource={Object.keys(selectedUserList)}
          renderItem={(key) => {
            const item = selectedUserList[key];
            return (
              <List.Item
                actions={[
                  <InputNumber
                    key="coupon-amount"
                    min={1}
                    value={item.count}
                    onChange={(value) =>
                      setSelectedUserList({
                        ...selectedUserList,
                        [item.id]: { ...item, count: value },
                      })
                    }
                  />,
                  <Button
                    key="delete"
                    danger
                    onClick={() => onDeleteUser(item.id)}
                  >
                    {i18n.t("sales.activity.delete")}
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  avatar={
                    <Avatar shape="circle" size="large" src={item.avatar} />
                  }
                  title={item.nickname}
                  description={item.mobile}
                />
              </List.Item>
            );
          }}
        />
      </Row>
    </Modal>
  );
}

export default SendTicketForm;
